import { z } from 'zod';

// =============================================================================
// Day of Week
// =============================================================================

export const dayOfWeekSchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'weekdays',
  'weekends',
  'any',
]);

export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

// =============================================================================
// Time Slot
// =============================================================================

export const timeSlotSchema = z.object({
  startTime: z.string(), // "09:00" (24hr format)
  endTime: z.string(), // "17:00"
});

export type TimeSlot = z.infer<typeof timeSlotSchema>;

// =============================================================================
// Call Window
// =============================================================================

export const callWindowSchema = z.object({
  dayOfWeek: dayOfWeekSchema,
  slots: z.array(timeSlotSchema),
});

export type CallWindow = z.infer<typeof callWindowSchema>;

// =============================================================================
// Communication Channel
// =============================================================================

export const communicationChannelSchema = z.enum(['phone', 'sms', 'email']);

export type CommunicationChannel = z.infer<typeof communicationChannelSchema>;

// =============================================================================
// Generic Search Request
// =============================================================================

/**
 * Body of the generic `POST /search` endpoints (users, patients). The zod twin
 * of `SearchRequest` in `@vagusx/shared`, which callers type against.
 */
export const searchQuerySchema = z.object({
  field: z.string(),
  operator: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'like', 'in']),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
});

export const searchRequestSchema = z.object({
  queries: z.array(searchQuerySchema).default([]),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  include: z.array(z.string()).optional(),
});
